import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { PRICE_POINTS } from '../../data/conjointData';
import { formatPrice } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Props { selectedPrice: number; }

export function ElasticityCurve({ selectedPrice }: Props) {
  const isMobile = useIsMobile();
  const data = PRICE_POINTS.map(pt => ({ price: pt.price, choiceRate: pt.choiceRate * 100, revenuePerShowing: pt.revenuePerShowing ?? 0 }));
  const maxRevPrice = data.reduce((best, pt) => pt.revenuePerShowing > best.revenuePerShowing ? pt : best, data[0]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Price Elasticity Curve</h3>
        <InfoButton helpKey="elasticityCurve" size={13} />
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
        <LineChart data={data} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="price" {...X_AXIS_PROPS} tickFormatter={v => formatPrice(v)} />
          <YAxis yAxisId="left" {...Y_AXIS_PROPS} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
          <YAxis yAxisId="right" orientation="right" {...Y_AXIS_PROPS} tickFormatter={v => `$${v.toFixed(0)}`} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number, name: string) => { if (name === 'choiceRate') return [`${value.toFixed(1)}%`, 'Choice Rate']; return [`$${value.toFixed(0)}`, 'Rev / Prospect']; }} labelFormatter={v => formatPrice(Number(v))} />
          <ReferenceLine x={selectedPrice} yAxisId="left" stroke="#14B8A6" strokeDasharray="4 4" strokeWidth={2} />
          <ReferenceLine x={maxRevPrice.price} yAxisId="left" stroke="#F59E0B" strokeDasharray="4 4" strokeWidth={1} label={{ value: 'Max Rev', fill: '#F59E0B', fontSize: 11, position: 'top' }} />
          <Line yAxisId="left" type="monotone" dataKey="choiceRate" stroke={CHART_COLORS.teal} strokeWidth={2.5} dot={{ r: 5, fill: CHART_COLORS.teal, stroke: 'var(--color-dot-stroke)', strokeWidth: 2 }} activeDot={{ r: 7 }} />
          <Line yAxisId="right" type="monotone" dataKey="revenuePerShowing" stroke={CHART_COLORS.amber} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: CHART_COLORS.amber, stroke: 'var(--color-dot-stroke)', strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-6 mt-3 text-xs text-muted">
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-chart-teal rounded" /> Choice Rate (left axis)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-chart-amber rounded border-dashed" /> Revenue / Prospect (right)<InfoButton helpKey="revenuePerShowing" size={11} /></span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 border-t-2 border-dashed border-chart-teal rounded" /> Selected Price</span>
      </div>
    </div>
  );
}
